import type { LabColor, RgbColor, XyzColor } from './types';

const REF_WHITE_D65: XyzColor = {
  x: 0.95047,
  y: 1.0,
  z: 1.08883,
};

const SRGB_TO_XYZ_D65 = [
  [0.4124564, 0.3575761, 0.1804375],
  [0.2126729, 0.7151522, 0.072175],
  [0.0193339, 0.119192, 0.9503041],
] as const;

const XYZ_TO_SRGB_D65 = [
  [3.2404542, -1.5371385, -0.4985314],
  [-0.969266, 1.8760108, 0.041556],
  [0.0556434, -0.2040259, 1.0572252],
] as const;

const DELTA = 6 / 29;
const DELTA_CUBED = DELTA * DELTA * DELTA;
const KAPPA_INV = (29 / 6) * (29 / 6) * (1 / 3);

function srgbChannelToLinear(channel: number): number {
  const v = channel / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function linearChannelToSrgb(linear: number): number {
  const v = linear <= 0.0031308 ? 12.92 * linear : 1.055 * Math.pow(linear, 1 / 2.4) - 0.055;
  return Math.max(0, Math.min(255, Math.round(v * 255)));
}

function labF(t: number): number {
  return t > DELTA_CUBED ? Math.cbrt(t) : KAPPA_INV * t + 4 / 29;
}

function labFInverse(t: number): number {
  return t > DELTA ? t * t * t : 3 * DELTA * DELTA * (t - 4 / 29);
}

export function rgbToXyz(rgb: RgbColor): XyzColor {
  const rLin = srgbChannelToLinear(rgb.r);
  const gLin = srgbChannelToLinear(rgb.g);
  const bLin = srgbChannelToLinear(rgb.b);

  const m = SRGB_TO_XYZ_D65;
  return {
    x: m[0][0] * rLin + m[0][1] * gLin + m[0][2] * bLin,
    y: m[1][0] * rLin + m[1][1] * gLin + m[1][2] * bLin,
    z: m[2][0] * rLin + m[2][1] * gLin + m[2][2] * bLin,
  };
}

export function xyzToRgb(xyz: XyzColor): RgbColor {
  const m = XYZ_TO_SRGB_D65;
  const rLin = m[0][0] * xyz.x + m[0][1] * xyz.y + m[0][2] * xyz.z;
  const gLin = m[1][0] * xyz.x + m[1][1] * xyz.y + m[1][2] * xyz.z;
  const bLin = m[2][0] * xyz.x + m[2][1] * xyz.y + m[2][2] * xyz.z;

  return {
    r: linearChannelToSrgb(rLin),
    g: linearChannelToSrgb(gLin),
    b: linearChannelToSrgb(bLin),
  };
}

export function xyzToLab(xyz: XyzColor): LabColor {
  const fx = labF(xyz.x / REF_WHITE_D65.x);
  const fy = labF(xyz.y / REF_WHITE_D65.y);
  const fz = labF(xyz.z / REF_WHITE_D65.z);

  return {
    l: 116 * fy - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  };
}

export function labToXyz(lab: LabColor): XyzColor {
  const fy = (lab.l + 16) / 116;
  const fx = lab.a / 500 + fy;
  const fz = fy - lab.b / 200;

  return {
    x: REF_WHITE_D65.x * labFInverse(fx),
    y: REF_WHITE_D65.y * labFInverse(fy),
    z: REF_WHITE_D65.z * labFInverse(fz),
  };
}

export function rgbToLab(rgb: RgbColor): LabColor {
  return xyzToLab(rgbToXyz(rgb));
}

export function labToRgb(lab: LabColor): RgbColor {
  return xyzToRgb(labToXyz(lab));
}
